import type { Passage } from "./types";

export const passage: Passage = {
  slug: "philippians-4-6-7",
  reference: "Philippians 4:6–7",
  title: "Philippians 4:6–7 meaning: it is not “stop worrying”",
  h1: "Philippians 4:6–7, which is not an order to feel differently",
  description:
    "The anxiety verse people are handed most often. It does not tell you to stop being anxious — it tells you what to do with it, which is a possible instruction at 3am.",
  targets: [
    "philippians 4:6 meaning",
    "do not be anxious about anything explained",
    "philippians 4 6 7 how to pray",
  ],
  published: "2026-08-27",
  standfirst:
    "It is the verse most often quoted at anxious people, and the reason it so often lands badly is that it is usually quoted as half a sentence.",
  verse: {
    text: "In nothing be anxious, but in everything, by prayer and petition with thanksgiving, let your requests be made known to God. And the peace of God, which surpasses all understanding, will guard your hearts and your thoughts in Christ Jesus.",
    ref: "Philippians 4:6–7",
  },
  context: [
    {
      text: "Rejoice in the Lord always! Again I will say, 'Rejoice!' Let your gentleness be known to all men. The Lord is at hand.",
      ref: "Philippians 4:4–5",
    },
    {
      text: "Finally, brothers, whatever things are true, whatever things are honorable … think about these things.",
      ref: "Philippians 4:8",
    },
  ],
  takeaways: [
    "The sentence has a 'but' in the middle: the instruction is not to stop being anxious but to convert the anxiety into a specific request.",
    "Thanksgiving is part of the mechanism rather than politeness — it is the clause that stops the prayer from becoming a rehearsal of the problem.",
    "The peace is described as a guard posted around the heart and thoughts, not as an explanation and not as a feeling of calm arriving.",
    "It surpasses understanding, which means it is not waiting for the situation to make sense first — a detail that matters at three in the morning.",
  ],
  body: [
    {
      type: "h2",
      id: "the-instruction",
      text: "The instruction is an action, not a feeling",
    },
    {
      type: "p",
      text: "Read as an order to feel differently, this verse is impossible and slightly cruel: nobody has ever stopped being anxious by being told to. But the sentence does not end where it is usually quoted. It has a hinge in the middle — in nothing be anxious, BUT in everything, by prayer and petition with thanksgiving, let your requests be made known.",
    },
    {
      type: "p",
      text: "The second half is the instruction, and it is something a person can actually do while frightened. Take the thing, make it specific, say it. That is a motor task rather than an emotional one, which is precisely why it works at the hours when nothing emotional is under your control.",
    },
    {
      type: "h2",
      id: "the-thanksgiving-clause",
      text: "Why thanksgiving is in there",
    },
    {
      type: "p",
      text: "It reads like a manners requirement and it is doing something structural. Anxious prayer has a strong tendency to become a review of the situation — you go around the problem again, in more detail, with God as the audience — and a review is exactly what keeps the loop running. Adding one thing to be grateful for interrupts the circuit; it forces the mind out of the problem for the length of a sentence.",
    },
    {
      type: "p",
      text: "The other function is honesty. Gratitude in the middle of a request keeps the prayer from becoming an accusation, without requiring you to pretend the situation is fine.",
    },
    {
      type: "h2",
      id: "the-guard",
      text: "What the peace is described as doing",
    },
    {
      type: "p",
      text: "Not filling, not arriving, not explaining — guarding. The word is a sentry image, and it is posted around two specific things: your hearts and your thoughts. That is a claim about a perimeter rather than about the contents, which is a strikingly modest promise and a far more useful one.",
    },
    {
      type: "p",
      text: "And it surpasses understanding, which in this context is not a compliment about how wonderful it is. It means the peace does not depend on the situation being resolved or explained first. You can have the guard posted while still having no idea how this ends.",
    },
  ],
  misreading: {
    claim:
      "That it commands you not to feel anxious — and therefore that anxiety is a failure of faith to be repented of.",
    correction: [
      "This is the reading that makes people stop reading. Told that the feeling itself is disobedience, an anxious person now has two problems, and the second one is unfixable by definition. It is also not what the sentence says: the imperative in the second half is to let your requests be made known, and the first half describes what you are being moved away from rather than a state you are being fined for occupying.",
      "The rest of the New Testament is not consistent with the accusatory reading either. 1 Peter 5:7 says to throw your worries onto him because he cares for you, which assumes there are worries to throw and gives a reason that is about care rather than about correction. Psalm 56:3 assumes the fear will happen and describes what is done inside it.",
      "There is a real instruction here and it is demanding enough without the addition. Convert every anxiety, in everything, into a specific request with something to be thankful for attached, every time it comes back — and it will come back. That is a discipline, not a mood, and it is what the passage is asking for.",
    ],
  },
  use: [
    {
      type: "h2",
      id: "how-to-use-it",
      text: "How to use it at three in the morning",
    },
    {
      type: "ol",
      items: [
        "Name the thing in one sentence. Not the whole situation — the specific request inside it.",
        "Say it, out loud if you can, with one thing you are thankful for attached to it.",
        "When it comes back in four minutes, say it again. Repetition is not failure here; the passage says in everything, which implies more than once.",
        "Do not wait to feel the peace before you stop. It is described as a guard being posted, not as a sensation confirming the prayer worked.",
      ],
    },
    {
      type: "p",
      text: "It is short enough to memorise and the memorising is the whole difference, because nobody looks up a page at that hour. The rest of the passages for those hours are in {the-night-collection}, and several of them are about lying down specifically rather than about fear in general.",
      links: {
        "the-night-collection": "/verses/bible-verses-for-anxiety-at-night",
      },
    },
    {
      type: "note",
      text: "If the nights are most nights, this passage sits alongside seeing a doctor rather than instead of it. That is not a lack of faith; it is the same practical wisdom the letter itself keeps showing.",
    },
  ],
  faq: [
    {
      q: "Does Philippians 4:6 mean anxiety is a sin?",
      a: "The sentence is built around an instruction to do something with the anxiety — make the request known, with thanksgiving — rather than a verdict on having it. Read as a prohibition on the feeling it becomes unfollowable, and 1 Peter 5:7 does not support that reading: it assumes there are worries.",
    },
    {
      q: "What does 'the peace that surpasses all understanding' mean?",
      a: "That it does not require the situation to be explained or resolved first. The verse describes it as a guard posted around the heart and the thoughts, which is a claim about protection rather than about a feeling of calm.",
    },
    {
      q: "How do I pray Philippians 4:6 when my mind is racing?",
      a: "One specific request rather than a review of the whole situation, said in a sentence, with one thing to be thankful for attached. When it returns, say it again. The review is what keeps the loop going; the request is what the passage asks for instead.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain. Familiar renderings say 'do not be anxious about anything'; the structure of the sentence, and the hinge in the middle, is the same in all of them.",
    },
  ],
  related: [
    "how-to-stop-scrolling-at-night",
    "how-to-memorize-bible-verses",
    "morning-quiet-time-routine",
  ],
  relatedTopics: [
    "bible-verses-for-anxiety-at-night",
    "bible-verses-about-rest",
  ],
  relatedPassages: ["isaiah-41-10", "philippians-4-13"],
};
